import { useState, useEffect, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  DollarSign,
  CreditCard,
  AlertTriangle,
  Clock,
  TrendingUp,
  Users,
  FileText,
  Search,
  Plus,
  Building2,
  Calendar,
  Mail,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Filter,
  Download,
  Upload,
  Send,
  Percent,
  RefreshCw,
  Eye,
  Edit,
  Ban,
  MessageSquare,
  Save,
  Settings,
  Play,
  Pause,
  Inbox,
  UserCheck,
  Trash2,
  Power,
  ToggleLeft,
  ToggleRight,
  Loader2,
} from "lucide-react";


const STATUS_COLORS = {
  open: "bg-blue-100 text-blue-800",
  partial: "bg-yellow-100 text-yellow-800",
  paid: "bg-green-100 text-green-800",
  overdue: "bg-red-100 text-red-800",
  payment: "bg-purple-100 text-purple-800",  // Unallocated payments
  active: "bg-green-100 text-green-800",
  suspended: "bg-red-100 text-red-800",
  expired: "bg-gray-100 text-gray-800",
};

export default function FinancePage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(false);
  const [sendingSoaFor, setSendingSoaFor] = useState(null); // Track which company is sending SOA
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  
  // Credit Agreements
  const [agreements, setAgreements] = useState([]);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [editingAgreement, setEditingAgreement] = useState(null);
  const [agreementForm, setAgreementForm] = useState({
    company_id: "",
    company_name: "",
    credit_limit: 0,
    payment_terms_days: 30,
    currency: "EUR",
    notes: "",
    valid_from: "",
    valid_until: "",
  });
  
  // A/R Data
  const [arEntries, setArEntries] = useState([]);
  const [arSummary, setArSummary] = useState(null);
  const [arPage, setArPage] = useState(1);
  const [arTotalPages, setArTotalPages] = useState(1);
  const [arFilter, setArFilter] = useState({ status: "", search: "" });
  
  // Grouped A/R by customer
  const [customerGroups, setCustomerGroups] = useState([]);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [customerNotes, setCustomerNotes] = useState({});
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState("");
  
  // Company search for agreements
  const [companySearch, setCompanySearch] = useState("");
  const [companyResults, setCompanyResults] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  
  // SOA Reminder
  const [showSOAModal, setShowSOAModal] = useState(false);
  const [soaData, setSoaData] = useState({ company_id: "", email_to: "", include_discount: false });
  
  // A/R Entry Modal
  const [showARModal, setShowARModal] = useState(false);
  const [arForm, setArForm] = useState({
    company_id: "",
    company_name: "",
    invoice_number: "",
    invoice_date: new Date().toISOString().slice(0, 10),
    due_date: "",
    amount: 0,
    currency: "EUR",
    pf_number: "",
    notes: "",
  });
  
  // Import Modal
  const [showImportModal, setShowImportModal] = useState(false);
  
  // Email Flow Control
  const [emailSettings, setEmailSettings] = useState({
    soa_monday_enabled: false,
    overdue_wednesday_enabled: false,
    master_switch_enabled: true,
    last_soa_run: null,
    last_overdue_run: null
  });
  const [customerEmails, setCustomerEmails] = useState([]);
  const [customerToggles, setCustomerToggles] = useState([]);
  const [pendingEmails, setPendingEmails] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailForm, setEmailForm] = useState({ company_id: "", company_name: "", finance_email: "" });
  const [showPendingActionModal, setShowPendingActionModal] = useState(false);
  const [selectedPending, setSelectedPending] = useState(null);
  const [pendingAction, setPendingAction] = useState({ action: "send", new_email: "" });
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [toggleForm, setToggleForm] = useState({ company_id: "", company_name: "", soa_enabled: true, overdue_enabled: true });
  
  const token = localStorage.getItem("token");

  // Fetch dashboard
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/finance/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDashboard(response.data);
    } catch (error) {
      console.error("Failed to load finance dashboard", error);
    }
  }, [token]);

  // Fetch credit agreements
  const fetchAgreements = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/finance/credit-agreements`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAgreements(response.data.agreements);
    } catch (error) {
      toast.error("Failed to load credit agreements");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch A/R entries
  const fetchAREntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: arPage, per_page: 30 });
      if (arFilter.status) params.append("status", arFilter.status);
      if (arFilter.search) params.append("search", arFilter.search);
      
      const response = await axios.get(`${API_URL}/finance/accounts-receivable?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArEntries(response.data.entries);
      setArSummary(response.data.summary);
      setArTotalPages(response.data.total_pages);
      
      // Group entries by customer
      const grouped = {};
      response.data.entries.forEach(entry => {
        const key = entry.company_name || "Unknown";
        if (!grouped[key]) {
          grouped[key] = {
            company_name: entry.company_name,
            company_id: entry.company_id,
            invoices: [],
            total_outstanding: 0,
            total_overdue: 0,
            invoice_count: 0,
            overdue_count: 0,
          };
        }
        grouped[key].invoices.push(entry);
        grouped[key].invoice_count++;
        if (entry.status !== 'paid') {
          grouped[key].total_outstanding += entry.balance || 0;
        }
        if (entry.days_overdue > 0) {
          grouped[key].total_overdue += entry.balance || 0;
          grouped[key].overdue_count++;
        }
      });
      
      // Sort by outstanding amount descending
      const sortedGroups = Object.values(grouped).sort((a, b) => b.total_outstanding - a.total_outstanding);
      setCustomerGroups(sortedGroups);
    } catch (error) {
      toast.error("Failed to load A/R entries");
    } finally {
      setLoading(false);
    }
  }, [token, arPage, arFilter]);

  // Fetch customer notes
  const fetchCustomerNotes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/finance/customer-notes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const notesMap = {};
      (response.data.notes || []).forEach(n => {
        notesMap[n.company_name] = n.note;
      });
      setCustomerNotes(notesMap);
    } catch (error) {
      console.error("Failed to load customer notes", error);
    }
  }, [token]);

  // Save customer note
  const saveCustomerNote = async (companyName) => {
    try {
      await axios.post(`${API_URL}/finance/customer-notes`, {
        company_name: companyName,
        note: noteText,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomerNotes(prev => ({ ...prev, [companyName]: noteText }));
      setEditingNote(null);
      setNoteText("");
      toast.success("Note saved");
    } catch (error) {
      toast.error("Failed to save note");
    }
  };

  // Export A/R data
  const exportARData = async (format = "csv") => {
    try {
      const response = await axios.get(`${API_URL}/finance/accounts-receivable/export?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `accounts_receivable_${new Date().toISOString().slice(0,10)}.${format === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`A/R data exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error("Failed to export A/R data");
    }
  };

  // Export credit agreements
  const exportCreditAgreements = async (format = "csv") => {
    try {
      const response = await axios.get(`${API_URL}/finance/credit-agreements/export?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `credit_agreements_${new Date().toISOString().slice(0,10)}.${format === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Credit agreements exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error("Failed to export credit agreements");
    }
  };

  // Fetch email settings
  const fetchEmailSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/finance/email-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmailSettings(response.data);
    } catch (error) {
      console.error("Failed to load email settings", error);
    }
  }, [token]);

  // Fetch customer emails
  const fetchCustomerEmails = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/finance/customer-emails`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomerEmails(response.data.emails || []);
    } catch (error) {
      console.error("Failed to load customer emails", error);
    }
  }, [token]);

  // Fetch pending emails
  const fetchPendingEmails = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/finance/pending-emails`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPendingEmails(response.data.pending_emails || []);
    } catch (error) {
      console.error("Failed to load pending emails", error);
    }
  }, [token]);

  // Fetch customer email toggles
  const fetchCustomerToggles = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/finance/customer-email-toggles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomerToggles(response.data.toggles || []);
    } catch (error) {
      console.error("Failed to load customer toggles", error);
    }
  }, [token]);

  // Update email flow settings
  const updateEmailSettings = async (newSettings) => {
    try {
      await axios.put(`${API_URL}/finance/email-settings`, newSettings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmailSettings(prev => ({ ...prev, ...newSettings }));
      toast.success("Email flow settings updated");
    } catch (error) {
      toast.error("Failed to update settings");
    }
  };

  // Save customer toggle
  const saveCustomerToggle = async () => {
    if (!toggleForm.company_name) {
      toast.error("Please select a company");
      return;
    }
    try {
      await axios.post(`${API_URL}/finance/customer-email-toggles`, toggleForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Email settings saved for ${toggleForm.company_name}`);
      setShowToggleModal(false);
      setToggleForm({ company_id: "", company_name: "", soa_enabled: true, overdue_enabled: true });
      fetchCustomerToggles();
    } catch (error) {
      toast.error("Failed to save customer toggle");
    }
  };

  // Delete customer toggle
  const deleteCustomerToggle = async (companyId, companyName) => {
    if (!window.confirm(`Remove custom settings for ${companyName}? They will receive emails by default.`)) return;
    try {
      await axios.delete(`${API_URL}/finance/customer-email-toggles/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Custom settings removed");
      fetchCustomerToggles();
    } catch (error) {
      toast.error("Failed to remove settings");
    }
  };

  // Save customer email
  const saveCustomerEmail = async () => {
    if (!emailForm.company_name || !emailForm.finance_email) {
      toast.error("Please fill in all fields");
      return;
    }
    try {
      await axios.post(`${API_URL}/finance/customer-emails`, emailForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Customer email saved");
      setShowEmailModal(false);
      setEmailForm({ company_id: "", company_name: "", finance_email: "" });
      fetchCustomerEmails();
    } catch (error) {
      toast.error("Failed to save customer email");
    }
  };

  // Delete customer email
  const deleteCustomerEmail = async (companyId) => {
    if (!window.confirm("Remove this customer's finance email?")) return;
    try {
      await axios.delete(`${API_URL}/finance/customer-emails/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Customer email removed");
      fetchCustomerEmails();
    } catch (error) {
      toast.error("Failed to remove email");
    }
  };

  // Handle pending email action
  const handlePendingAction = async () => {
    if (!selectedPending) return;
    try {
      await axios.post(`${API_URL}/finance/pending-emails/${selectedPending.id}/action`, pendingAction, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(pendingAction.action === "skip" ? "Email skipped" : "Email sent");
      setShowPendingActionModal(false);
      setSelectedPending(null);
      setPendingAction({ action: "send", new_email: "" });
      fetchPendingEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    }
  };

  // Trigger batch emails
  const triggerBatchEmails = async (type) => {
    setLoading(true);
    try {
      const endpoint = type === "soa" ? "/finance/trigger-soa-batch" : "/finance/trigger-overdue-batch";
      const response = await axios.post(`${API_URL}${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(response.data.message);
      fetchPendingEmails();
      fetchEmailSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to trigger batch emails");
    } finally {
      setLoading(false);
    }
  };

  // Trigger SOA for single company
  const triggerSingleSOA = async (companyName) => {
    setSendingSoaFor(companyName);
    try {
      const response = await axios.post(
        `${API_URL}/finance/trigger-soa-single`,
        { company_name: companyName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.info(response.data.message);
        fetchPendingEmails(); // Refresh pending emails if added to queue
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send SOA");
    } finally {
      setSendingSoaFor(null);
    }
  };

  // Search companies for credit agreement
  const searchCompanies = async (query) => {
    if (!query || query.length < 2) {
      setCompanyResults([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/crm/companies?search=${encodeURIComponent(query)}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanyResults(response.data.companies || []);
      setShowCompanyDropdown(true);
    } catch (error) {
      console.error("Company search failed", error);
    }
  };

  // Create/Update credit agreement
  const saveAgreement = async () => {
    if (!agreementForm.company_id || !agreementForm.company_name || agreementForm.credit_limit <= 0) {
      toast.error("Please select a company and set credit limit");
      return;
    }
    
    try {
      if (editingAgreement) {
        await axios.put(
          `${API_URL}/finance/credit-agreements/${editingAgreement.id}`,
          agreementForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Credit agreement updated");
      } else {
        await axios.post(`${API_URL}/finance/credit-agreements`, agreementForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Credit agreement created");
      }
      setShowAgreementModal(false);
      setEditingAgreement(null);
      setAgreementForm({
        company_id: "",
        company_name: "",
        credit_limit: 0,
        payment_terms_days: 30,
        currency: "EUR",
        notes: "",
        valid_from: "",
        valid_until: "",
      });
      fetchAgreements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save credit agreement");
    }
  };

  // Create A/R entry
  const saveAREntry = async () => {
    if (!arForm.company_name || !arForm.invoice_number || arForm.amount <= 0 || !arForm.due_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    try {
      await axios.post(`${API_URL}/finance/accounts-receivable`, arForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Invoice added to A/R");
      setShowARModal(false);
      setArForm({
        company_id: "",
        company_name: "",
        invoice_number: "",
        invoice_date: new Date().toISOString().slice(0, 10),
        due_date: "",
        amount: 0,
        currency: "EUR",
        pf_number: "",
        notes: "",
      });
      fetchAREntries();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add invoice");
    }
  };

  // Send SOA reminder
  const sendSOAReminder = async () => {
    if (!soaData.company_id || !soaData.email_to) {
      toast.error("Please select company and enter email");
      return;
    }
    
    try {
      await axios.post(`${API_URL}/finance/send-soa-reminder`, {
        company_id: soaData.company_id,
        email_to: soaData.email_to,
        include_discount_option: soaData.include_discount,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`SOA reminder sent to ${soaData.email_to}`);
      setShowSOAModal(false);
      setSoaData({ company_id: "", email_to: "", include_discount: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder");
    }
  };

  // Import A/R file
  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await axios.post(`${API_URL}/finance/accounts-receivable/bulk-import`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported: ${response.data.created} new, ${response.data.updated} updated`);
      setShowImportModal(false);
      fetchAREntries();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    }
  };

  // Mark payment
  const markPayment = async (entryId, amount, currentPaid) => {
    const newPaid = prompt(`Enter payment amount (current paid: ${currentPaid})`, amount);
    if (newPaid === null) return;
    
    try {
      await axios.put(
        `${API_URL}/finance/accounts-receivable/${entryId}`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { paid_amount: parseFloat(newPaid) },
        }
      );
      toast.success("Payment recorded");
      fetchAREntries();
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to record payment");
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchAgreements();
    fetchAREntries();
    fetchEmailSettings();
    fetchCustomerEmails();
    fetchPendingEmails();
    fetchCustomerToggles();
    fetchCustomerNotes();
  }, [fetchDashboard, fetchAgreements, fetchAREntries, fetchEmailSettings, fetchCustomerEmails, fetchPendingEmails, fetchCustomerToggles, fetchCustomerNotes]);

  useEffect(() => {
    fetchAREntries();
  }, [arPage, arFilter, fetchAREntries]);

  const formatCurrency = (amount, currency = "EUR") => {
    return new Intl.NumberFormat("en-EU", { style: "currency", currency }).format(amount);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8 pt-24">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-700 to-emerald-600 rounded-2xl p-8 mb-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="h-8 w-8" />
                <h1 className="text-3xl font-bold">Finance Module</h1>
              </div>
              <p className="text-emerald-100">
                Credit agreements, Accounts Receivable & SOA Management
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={() => setShowAgreementModal(true)}
                className="bg-white/20 hover:bg-white/30 border border-white/30"
                data-testid="new-credit-agreement-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Agreement
              </Button>
              <Button
                onClick={() => setShowARModal(true)}
                className="bg-white/20 hover:bg-white/30 border border-white/30"
                data-testid="add-invoice-btn"
              >
                <FileText className="h-4 w-4 mr-2" />
                Add Invoice
              </Button>
              <Button
                onClick={() => setShowImportModal(true)}
                className="bg-white/20 hover:bg-white/30 border border-white/30"
                data-testid="import-ar-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                Import
              </Button>
              <Button
                onClick={() => exportARData("csv")}
                className="bg-white/20 hover:bg-white/30 border border-white/30"
                data-testid="export-ar-csv-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
              <Button
                onClick={() => exportARData("excel")}
                className="bg-white/20 hover:bg-white/30 border border-white/30"
                data-testid="export-ar-excel-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Export Excel
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: "dashboard", label: "Dashboard", icon: TrendingUp },
            { id: "credit", label: "Credit Agreements", icon: CreditCard },
            { id: "ar", label: "Accounts Receivable", icon: FileText },
            { id: "email", label: "Email Settings", icon: Settings, badge: pendingEmails.length > 0 ? pendingEmails.length : null },
          ].map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "default" : "outline"}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-2 relative"
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.badge && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {tab.badge}
                </span>
              )}
            </Button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === "dashboard" && dashboard && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-6 border shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Outstanding</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {formatCurrency(dashboard.total_outstanding)}
                    </p>
                    <p className="text-xs text-slate-400">{dashboard.outstanding_count} invoices</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-6 border shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Overdue</p>
                    <p className="text-2xl font-bold text-red-600">
                      {formatCurrency(dashboard.total_overdue)}
                    </p>
                    <p className="text-xs text-slate-400">{dashboard.overdue_count} invoices</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-6 border shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Due in 7 Days</p>
                    <p className="text-2xl font-bold text-amber-600">
                      {formatCurrency(dashboard.due_soon)}
                    </p>
                    <p className="text-xs text-slate-400">{dashboard.due_soon_count} invoices</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <Clock className="h-6 w-6 text-amber-600" />
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-6 border shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Credit Agreements</p>
                    <p className="text-2xl font-bold text-emerald-600">
                      {dashboard.active_credit_agreements}
                    </p>
                    <p className="text-xs text-slate-400">active</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CreditCard className="h-6 w-6 text-emerald-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Top Customers */}
            <div className="bg-white rounded-xl p-6 border shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Users className="h-5 w-5 text-slate-500" />
                Top Customers by Outstanding Balance
              </h3>
              <div className="space-y-3">
                {dashboard.top_customers?.map((customer, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-slate-500 w-6">{idx + 1}.</span>
                      <span className="font-medium text-slate-900">{customer.name}</span>
                      <span className="text-xs text-slate-400">({customer.invoice_count} invoices)</span>
                    </div>
                    <span className="font-semibold text-slate-900">{formatCurrency(customer.outstanding)}</span>
                  </div>
                ))}
                {(!dashboard.top_customers || dashboard.top_customers.length === 0) && (
                  <p className="text-slate-400 text-center py-4">No outstanding balances</p>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Credit Agreements Tab */}
        {activeTab === "credit" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="font-semibold text-slate-900">Credit Agreements</h3>
                <Button onClick={() => setShowAgreementModal(true)} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  New Agreement
                </Button>
              </div>
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3 text-xs font-medium text-slate-500">Company</th>
                    <th className="text-right p-3 text-xs font-medium text-slate-500">Credit Limit</th>
                    <th className="text-right p-3 text-xs font-medium text-slate-500">Current Balance</th>
                    <th className="text-right p-3 text-xs font-medium text-slate-500">Available</th>
                    <th className="text-right p-3 text-xs font-medium text-slate-500">Overdue</th>
                    <th className="text-center p-3 text-xs font-medium text-slate-500">Terms</th>
                    <th className="text-center p-3 text-xs font-medium text-slate-500">Status</th>
                    <th className="text-center p-3 text-xs font-medium text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {agreements.map((agr) => (
                    <tr key={agr.id} className="border-b hover:bg-slate-50">
                      <td className="p-3">
                        <div className="font-medium text-slate-900">{agr.company_name}</div>
                      </td>
                      <td className="p-3 text-right font-medium">{formatCurrency(agr.credit_limit)}</td>
                      <td className="p-3 text-right">{formatCurrency(agr.current_balance)}</td>
                      <td className="p-3 text-right">
                        <span className={agr.available_credit < 0 ? "text-red-600 font-semibold" : "text-emerald-600"}>
                          {formatCurrency(agr.available_credit)}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        {agr.overdue_amount > 0 ? (
                          <span className="text-red-600 font-semibold">{formatCurrency(agr.overdue_amount)}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="p-3 text-center">{agr.payment_terms_days} days</td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[agr.status]}`}>
                          {agr.status}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingAgreement(agr);
                              setAgreementForm({
                                company_id: agr.company_id,
                                company_name: agr.company_name,
                                credit_limit: agr.credit_limit,
                                payment_terms_days: agr.payment_terms_days,
                                currency: agr.currency,
                                notes: agr.notes || "",
                                valid_from: agr.valid_from || "",
                                valid_until: agr.valid_until || "",
                              });
                              setShowAgreementModal(true);
                            }}
                            data-testid={`edit-agreement-${agr.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSoaData({ company_id: agr.company_id, email_to: "", include_discount: false });
                              setShowSOAModal(true);
                            }}
                            data-testid={`send-soa-${agr.id}`}
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {agreements.length === 0 && (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-slate-400">
                        No credit agreements found. Create one to start managing customer credit.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Accounts Receivable Tab */}
        {activeTab === "ar" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {/* Summary */}
            {arSummary && (
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-4 border">
                  <p className="text-sm text-slate-500">Total Outstanding</p>
                  <p className="text-xl font-bold">{formatCurrency(arSummary.total_outstanding)}</p>
                </div>
                <div className="bg-white rounded-lg p-4 border">
                  <p className="text-sm text-slate-500">Total Overdue</p>
                  <p className="text-xl font-bold text-red-600">{formatCurrency(arSummary.total_overdue)}</p>
                </div>
                <div className="bg-white rounded-lg p-4 border">
                  <p className="text-sm text-slate-500">Overdue Invoices</p>
                  <p className="text-xl font-bold text-red-600">{arSummary.count_overdue}</p>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by company, invoice, PF..."
                  className="pl-10"
                  value={arFilter.search}
                  onChange={(e) => setArFilter((f) => ({ ...f, search: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && fetchAREntries()}
                />
              </div>
              <select
                className="border rounded-lg px-4 py-2"
                value={arFilter.status}
                onChange={(e) => setArFilter((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="">All Statuses</option>
                <option value="open">Open</option>
                <option value="partial">Partial</option>
                <option value="overdue">Overdue</option>
                <option value="payment">Unallocated Payment</option>
                <option value="paid">Paid</option>
              </select>
              <Button variant="outline" onClick={fetchAREntries}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>

            {/* Grouped Customer List */}
            <div className="space-y-3">
              {customerGroups.map((group) => (
                <div key={group.company_name} className="bg-white rounded-xl border shadow-sm overflow-hidden">
                  {/* Customer Header - Clickable */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpandedCustomer(expandedCustomer === group.company_name ? null : group.company_name)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${group.overdue_count > 0 ? 'bg-red-100' : 'bg-blue-100'}`}>
                          <Building2 className={`h-5 w-5 ${group.overdue_count > 0 ? 'text-red-600' : 'text-blue-600'}`} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{group.company_name}</h3>
                          <div className="flex items-center gap-4 text-sm text-slate-500">
                            <span>{group.invoice_count} invoice{group.invoice_count !== 1 ? 's' : ''}</span>
                            {group.overdue_count > 0 && (
                              <span className="text-red-600 font-medium">{group.overdue_count} overdue</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {/* Send SOA Button */}
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-blue-300 text-blue-600 hover:bg-blue-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            triggerSingleSOA(group.company_name);
                          }}
                          disabled={sendingSoaFor === group.company_name}
                          data-testid={`send-soa-${group.company_name}`}
                        >
                          {sendingSoaFor === group.company_name ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                          ) : (
                            <Mail className="h-4 w-4 mr-1" />
                          )}
                          Send SOA
                        </Button>
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Outstanding</p>
                          <p className="text-xl font-bold">{formatCurrency(group.total_outstanding)}</p>
                        </div>
                        {group.total_overdue > 0 && (
                          <div className="text-right">
                            <p className="text-sm text-red-500">Overdue</p>
                            <p className="text-xl font-bold text-red-600">{formatCurrency(group.total_overdue)}</p>
                          </div>
                        )}
                        {expandedCustomer === group.company_name ? (
                          <ChevronUp className="h-5 w-5 text-slate-400" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-slate-400" />
                        )}
                      </div>
                    </div>
                    
                    {/* Internal Note */}
                    {customerNotes[group.company_name] && expandedCustomer !== group.company_name && (
                      <div className="mt-2 flex items-center gap-2 text-sm text-slate-500">
                        <MessageSquare className="h-3 w-3" />
                        <span className="italic truncate max-w-md">{customerNotes[group.company_name]}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Expanded Content - Invoice List */}
                  <AnimatePresence>
                    {expandedCustomer === group.company_name && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="border-t">
                          {/* Internal Note Section */}
                          <div className="p-4 bg-slate-50 border-b">
                            <div className="flex items-center gap-2 mb-2">
                              <MessageSquare className="h-4 w-4 text-slate-500" />
                              <span className="text-sm font-medium text-slate-600">Internal Note</span>
                            </div>
                            {editingNote === group.company_name ? (
                              <div className="flex gap-2">
                                <Textarea
                                  value={noteText}
                                  onChange={(e) => setNoteText(e.target.value)}
                                  placeholder="Add an internal note for this customer..."
                                  className="text-sm"
                                  rows={2}
                                />
                                <div className="flex flex-col gap-1">
                                  <Button size="sm" onClick={() => saveCustomerNote(group.company_name)}>
                                    <Save className="h-4 w-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => { setEditingNote(null); setNoteText(""); }}>
                                    <XCircle className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div 
                                className="p-2 bg-white rounded border cursor-pointer hover:bg-slate-50 min-h-[40px] text-sm text-slate-600"
                                onClick={() => { setEditingNote(group.company_name); setNoteText(customerNotes[group.company_name] || ""); }}
                              >
                                {customerNotes[group.company_name] || <span className="text-slate-400 italic">Click to add a note...</span>}
                              </div>
                            )}
                          </div>
                          
                          {/* Invoices Table */}
                          <table className="w-full">
                            <thead className="bg-slate-100">
                              <tr>
                                <th className="text-left p-3 text-xs font-medium text-slate-500">Invoice #</th>
                                <th className="text-left p-3 text-xs font-medium text-slate-500">PF #</th>
                                <th className="text-center p-3 text-xs font-medium text-slate-500">Invoice Date</th>
                                <th className="text-center p-3 text-xs font-medium text-slate-500">Due Date</th>
                                <th className="text-right p-3 text-xs font-medium text-slate-500">Amount</th>
                                <th className="text-right p-3 text-xs font-medium text-slate-500">Paid</th>
                                <th className="text-right p-3 text-xs font-medium text-slate-500">Balance</th>
                                <th className="text-center p-3 text-xs font-medium text-slate-500">Days Overdue</th>
                                <th className="text-center p-3 text-xs font-medium text-slate-500">Status</th>
                                <th className="text-center p-3 text-xs font-medium text-slate-500">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.invoices.map((entry) => (
                                <tr key={entry.id} className="border-b hover:bg-slate-50">
                                  <td className="p-3 font-medium">{entry.invoice_number}</td>
                                  <td className="p-3 text-sm text-slate-500">{entry.pf_number || "-"}</td>
                                  <td className="p-3 text-center text-sm">{entry.invoice_date?.slice(0, 10)}</td>
                                  <td className="p-3 text-center text-sm">{entry.due_date?.slice(0, 10)}</td>
                                  <td className="p-3 text-right">{formatCurrency(entry.amount, entry.currency)}</td>
                                  <td className="p-3 text-right">{formatCurrency(entry.paid_amount || 0, entry.currency)}</td>
                                  <td className="p-3 text-right font-medium">{formatCurrency(entry.balance, entry.currency)}</td>
                                  <td className="p-3 text-center">
                                    {entry.days_overdue > 0 ? (
                                      <span className="text-red-600 font-semibold">{entry.days_overdue}</span>
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                  <td className="p-3 text-center">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[entry.status]}`}>
                                      {entry.status}
                                    </span>
                                  </td>
                                  <td className="p-3 text-center">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => markPayment(entry.id, entry.balance, entry.paid_amount || 0)}
                                      disabled={entry.status === "paid"}
                                      data-testid={`mark-payment-${entry.id}`}
                                    >
                                      <CheckCircle className="h-4 w-4" />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
              
              {customerGroups.length === 0 && !loading && (
                <div className="bg-white rounded-xl border p-8 text-center text-slate-400">
                  No invoices found. Add invoices or import from file.
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Email Settings Tab */}
        {activeTab === "email" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Master Switch */}
            <div className={`rounded-xl p-6 border shadow-sm ${emailSettings.master_switch_enabled ? 'bg-white' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`h-12 w-12 rounded-full flex items-center justify-center ${emailSettings.master_switch_enabled ? 'bg-emerald-100' : 'bg-red-100'}`}>
                    <Power className={`h-6 w-6 ${emailSettings.master_switch_enabled ? 'text-emerald-600' : 'text-red-600'}`} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-slate-900">Master Switch</h3>
                    <p className="text-sm text-slate-500">
                      {emailSettings.master_switch_enabled 
                        ? "Email automation is ACTIVE - scheduled emails will be sent" 
                        : "Email automation is DISABLED - no automated emails will be sent"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => updateEmailSettings({ ...emailSettings, master_switch_enabled: !emailSettings.master_switch_enabled })}
                  className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${emailSettings.master_switch_enabled ? 'bg-emerald-600' : 'bg-red-500'}`}
                  data-testid="master-switch-toggle"
                >
                  <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${emailSettings.master_switch_enabled ? 'translate-x-7' : 'translate-x-1'}`} />
                </button>
              </div>
              {!emailSettings.master_switch_enabled && (
                <div className="mt-4 p-3 bg-red-100 rounded-lg text-red-800 text-sm flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  All automated SOA and Overdue emails are paused. Enable the master switch to resume.
                </div>
              )}
            </div>

            {/* Flow Controls with Hover Popups */}
            <div className="grid grid-cols-2 gap-6">
              {/* SOA Monday with Customer Popup */}
              <div className={`bg-white rounded-xl p-6 border shadow-sm relative group ${!emailSettings.master_switch_enabled ? 'opacity-50' : ''}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${emailSettings.soa_monday_enabled ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                      <Mail className={`h-5 w-5 ${emailSettings.soa_monday_enabled ? 'text-emerald-600' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">SOA Monday</h3>
                      <p className="text-sm text-slate-500">Statement of Account emails</p>
                    </div>
                  </div>
                  <button
                    onClick={() => updateEmailSettings({ ...emailSettings, soa_monday_enabled: !emailSettings.soa_monday_enabled })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${emailSettings.soa_monday_enabled ? 'bg-emerald-600' : 'bg-slate-200'}`}
                    data-testid="toggle-soa-monday"
                    disabled={!emailSettings.master_switch_enabled}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${emailSettings.soa_monday_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
                <div className="space-y-2 text-sm text-slate-600">
                  <p>Sends SOA to all customers with outstanding invoices every Monday at 8:00 AM CET</p>
                  {emailSettings.last_soa_run && (
                    <p className="text-xs text-slate-400">Last run: {new Date(emailSettings.last_soa_run).toLocaleString()}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <Button
                    onClick={() => triggerBatchEmails("soa")}
                    variant="outline"
                    size="sm"
                    disabled={loading}
                    data-testid="trigger-soa-batch"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Run Now
                  </Button>
                  {customerToggles.filter(t => !t.soa_enabled).length > 0 && (
                    <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                      {customerToggles.filter(t => !t.soa_enabled).length} excluded
                    </span>
                  )}
                </div>
                
                {/* Hover Popup for Customer-Specific Excludes */}
                <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-xl shadow-xl border p-4 z-20 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-sm text-slate-900 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Customer-Specific SOA Settings
                    </h4>
                    <Button size="sm" variant="ghost" onClick={() => setShowToggleModal(true)}>
                      <Plus className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </div>
                  {customerToggles.length > 0 ? (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {customerToggles.map(toggle => (
                        <div key={toggle.company_id} className="flex items-center justify-between text-sm py-1 px-2 rounded hover:bg-slate-50">
                          <span className="text-slate-700">{toggle.company_name}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${toggle.soa_enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                            {toggle.soa_enabled ? 'ON' : 'OFF'}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 text-center py-2">All customers use global setting</p>
                  )}
                </div>
              </div>

              {/* Overdue Wednesday with Customer Popup */}
              <div className={`bg-white rounded-xl p-6 border shadow-sm relative group ${!emailSettings.master_switch_enabled ? 'opacity-50' : ''}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${emailSettings.overdue_wednesday_enabled ? 'bg-red-100' : 'bg-slate-100'}`}>
                      <AlertTriangle className={`h-5 w-5 ${emailSettings.overdue_wednesday_enabled ? 'text-red-600' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Overdue Wednesday</h3>
                      <p className="text-sm text-slate-500">Overdue invoice reminders</p>
                    </div>
                  </div>
                  <button
                    onClick={() => updateEmailSettings({ ...emailSettings, overdue_wednesday_enabled: !emailSettings.overdue_wednesday_enabled })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${emailSettings.overdue_wednesday_enabled ? 'bg-red-600' : 'bg-slate-200'}`}
                    data-testid="toggle-overdue-wednesday"
                    disabled={!emailSettings.master_switch_enabled}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${emailSettings.overdue_wednesday_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
                <div className="space-y-2 text-sm text-slate-600">
                  <p>Sends overdue reminders to customers with past-due invoices every Wednesday at 8:00 AM CET</p>
                  {emailSettings.last_overdue_run && (
                    <p className="text-xs text-slate-400">Last run: {new Date(emailSettings.last_overdue_run).toLocaleString()}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <Button
                    onClick={() => triggerBatchEmails("overdue")}
                    variant="outline"
                    size="sm"
                    disabled={loading}
                    data-testid="trigger-overdue-batch"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Run Now
                  </Button>
                  {customerToggles.filter(t => !t.overdue_enabled).length > 0 && (
                    <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                      {customerToggles.filter(t => !t.overdue_enabled).length} excluded
                    </span>
                  )}
                </div>
                
                {/* Hover Popup for Customer-Specific Excludes */}
                <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-xl shadow-xl border p-4 z-20 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-sm text-slate-900 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Customer-Specific Overdue Settings
                    </h4>
                    <Button size="sm" variant="ghost" onClick={() => setShowToggleModal(true)}>
                      <Plus className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </div>
                  {customerToggles.length > 0 ? (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {customerToggles.map(toggle => (
                        <div key={toggle.company_id} className="flex items-center justify-between text-sm py-1 px-2 rounded hover:bg-slate-50">
                          <span className="text-slate-700">{toggle.company_name}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${toggle.overdue_enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                            {toggle.overdue_enabled ? 'ON' : 'OFF'}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 text-center py-2">All customers use global setting</p>
                  )}
                </div>
              </div>
            </div>

            {/* Pending Emails Queue - with limited display */}
            {pendingEmails.length > 0 && (
              <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
                <div className="p-4 border-b bg-amber-50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Inbox className="h-5 w-5 text-amber-600" />
                    <h3 className="font-semibold text-amber-900">Pending Emails - Needs Review</h3>
                    <span className="ml-2 bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full text-xs font-medium">
                      {pendingEmails.length}
                    </span>
                  </div>
                  <p className="text-xs text-amber-700">Emails queued because customer has no finance email on file</p>
                </div>
                <div className="divide-y max-h-80 overflow-y-auto">
                  {pendingEmails.slice(0, 10).map((pending) => (
                    <div key={pending.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${pending.type === 'soa' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}`}>
                            {pending.type === 'soa' ? 'SOA' : 'OVERDUE'}
                          </span>
                          <span className="font-medium text-slate-900">{pending.company_name}</span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{pending.subject}</p>
                        <p className="text-xs text-slate-400">Created: {new Date(pending.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedPending(pending);
                            setPendingAction({ action: "update_and_send", new_email: "" });
                            setShowPendingActionModal(true);
                          }}
                          data-testid={`resolve-pending-${pending.id}`}
                        >
                          <UserCheck className="h-4 w-4 mr-1" />
                          Set Email & Send
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedPending(pending);
                            setPendingAction({ action: "skip", new_email: "" });
                            handlePendingAction();
                          }}
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                {pendingEmails.length > 10 && (
                  <div className="p-3 bg-slate-50 text-center border-t">
                    <span className="text-sm text-slate-500">Showing 10 of {pendingEmails.length} pending emails. Scroll to see more or resolve above to reduce queue.</span>
                  </div>
                )}
              </div>
            )}

            {/* Customer Email Directory */}
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <div className="p-4 border-b flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-slate-500" />
                  <h3 className="font-semibold text-slate-900">Customer Finance Emails</h3>
                </div>
                <Button
                  onClick={() => setShowEmailModal(true)}
                  size="sm"
                  data-testid="add-customer-email-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Email
                </Button>
              </div>
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3 text-xs font-medium text-slate-500">Company</th>
                    <th className="text-left p-3 text-xs font-medium text-slate-500">Finance Email</th>
                    <th className="text-center p-3 text-xs font-medium text-slate-500">Last Updated</th>
                    <th className="text-center p-3 text-xs font-medium text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customerEmails.map((email) => (
                    <tr key={email.company_id} className="border-b hover:bg-slate-50">
                      <td className="p-3 font-medium text-slate-900">{email.company_name}</td>
                      <td className="p-3 text-slate-600">{email.finance_email}</td>
                      <td className="p-3 text-center text-sm text-slate-500">
                        {email.updated_at ? new Date(email.updated_at).toLocaleDateString() : "-"}
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEmailForm({
                                company_id: email.company_id,
                                company_name: email.company_name,
                                finance_email: email.finance_email
                              });
                              setShowEmailModal(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteCustomerEmail(email.company_id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {customerEmails.length === 0 && (
                    <tr>
                      <td colSpan={4} className="p-8 text-center text-slate-400">
                        No customer finance emails configured. Add emails to enable automated SOA sending.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>

      {/* Credit Agreement Modal */}
      {showAgreementModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="credit-agreement-modal">
          <div className="bg-white rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingAgreement ? "Edit Credit Agreement" : "New Credit Agreement"}
            </h3>
            
            <div className="space-y-4">
              {/* Company Search */}
              <div className="relative">
                <Label className="text-sm text-slate-600">Company *</Label>
                <Input
                  value={agreementForm.company_name}
                  onChange={(e) => {
                    setAgreementForm((f) => ({ ...f, company_name: e.target.value }));
                    searchCompanies(e.target.value);
                  }}
                  placeholder="Search company..."
                  className="mt-1"
                />
                {showCompanyDropdown && companyResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {companyResults.map((company) => (
                      <div
                        key={company.id}
                        className="p-2 hover:bg-slate-100 cursor-pointer"
                        onClick={() => {
                          setAgreementForm((f) => ({
                            ...f,
                            company_id: company.id,
                            company_name: company.name,
                          }));
                          setShowCompanyDropdown(false);
                        }}
                      >
                        <div className="font-medium">{company.name}</div>
                        {company.city && <div className="text-xs text-slate-500">{company.city}, {company.country}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-600">Credit Limit *</Label>
                  <Input
                    type="number"
                    value={agreementForm.credit_limit}
                    onChange={(e) => setAgreementForm((f) => ({ ...f, credit_limit: parseFloat(e.target.value) || 0 }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Currency</Label>
                  <select
                    value={agreementForm.currency}
                    onChange={(e) => setAgreementForm((f) => ({ ...f, currency: e.target.value }))}
                    className="w-full mt-1 h-10 px-3 border rounded-lg"
                  >
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-sm text-slate-600">Payment Terms (days)</Label>
                <Input
                  type="number"
                  value={agreementForm.payment_terms_days}
                  onChange={(e) => setAgreementForm((f) => ({ ...f, payment_terms_days: parseInt(e.target.value) || 30 }))}
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-600">Valid From</Label>
                  <Input
                    type="date"
                    value={agreementForm.valid_from?.slice(0, 10) || ""}
                    onChange={(e) => setAgreementForm((f) => ({ ...f, valid_from: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Valid Until</Label>
                  <Input
                    type="date"
                    value={agreementForm.valid_until?.slice(0, 10) || ""}
                    onChange={(e) => setAgreementForm((f) => ({ ...f, valid_until: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm text-slate-600">Notes</Label>
                <textarea
                  value={agreementForm.notes}
                  onChange={(e) => setAgreementForm((f) => ({ ...f, notes: e.target.value }))}
                  className="w-full mt-1 p-3 border rounded-lg resize-none"
                  rows={3}
                  placeholder="Internal notes about this credit agreement..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAgreementModal(false);
                  setEditingAgreement(null);
                }}
              >
                Cancel
              </Button>
              <Button onClick={saveAgreement} data-testid="save-agreement-btn">
                {editingAgreement ? "Update" : "Create"} Agreement
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* SOA Reminder Modal */}
      {showSOAModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="soa-modal">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Send SOA Reminder
            </h3>
            
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-slate-600">Email To *</Label>
                <Input
                  type="email"
                  value={soaData.email_to}
                  onChange={(e) => setSoaData((d) => ({ ...d, email_to: e.target.value }))}
                  placeholder="customer@company.com"
                  className="mt-1"
                />
              </div>

              <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <input
                  type="checkbox"
                  id="discount-option"
                  checked={soaData.include_discount}
                  onChange={(e) => setSoaData((d) => ({ ...d, include_discount: e.target.checked }))}
                  className="h-4 w-4"
                />
                <label htmlFor="discount-option" className="flex-1 cursor-pointer">
                  <div className="font-medium text-amber-800">Request 3% Cassa Sconto</div>
                  <div className="text-xs text-amber-600">Send discount prepayment request instead of standard reminder</div>
                </label>
                <Percent className="h-5 w-5 text-amber-600" />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowSOAModal(false)}>
                Cancel
              </Button>
              <Button onClick={sendSOAReminder} data-testid="send-soa-btn">
                <Send className="h-4 w-4 mr-2" />
                Send Reminder
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Invoice Modal */}
      {showARModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="ar-modal">
          <div className="bg-white rounded-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Add Invoice to A/R</h3>
            
            <div className="space-y-4">
              <div className="relative">
                <Label className="text-sm text-slate-600">Company *</Label>
                <Input
                  value={arForm.company_name}
                  onChange={(e) => {
                    setArForm((f) => ({ ...f, company_name: e.target.value }));
                    searchCompanies(e.target.value);
                  }}
                  placeholder="Search company..."
                  className="mt-1"
                />
                {showCompanyDropdown && companyResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {companyResults.map((company) => (
                      <div
                        key={company.id}
                        className="p-2 hover:bg-slate-100 cursor-pointer"
                        onClick={() => {
                          setArForm((f) => ({
                            ...f,
                            company_id: company.id,
                            company_name: company.name,
                          }));
                          setShowCompanyDropdown(false);
                        }}
                      >
                        {company.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-600">Invoice Number *</Label>
                  <Input
                    value={arForm.invoice_number}
                    onChange={(e) => setArForm((f) => ({ ...f, invoice_number: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600">PF Number</Label>
                  <Input
                    value={arForm.pf_number}
                    onChange={(e) => setArForm((f) => ({ ...f, pf_number: e.target.value }))}
                    placeholder="PF-000123-26"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-600">Invoice Date</Label>
                  <Input
                    type="date"
                    value={arForm.invoice_date}
                    onChange={(e) => setArForm((f) => ({ ...f, invoice_date: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Due Date *</Label>
                  <Input
                    type="date"
                    value={arForm.due_date}
                    onChange={(e) => setArForm((f) => ({ ...f, due_date: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-600">Amount *</Label>
                  <Input
                    type="number"
                    value={arForm.amount}
                    onChange={(e) => setArForm((f) => ({ ...f, amount: parseFloat(e.target.value) || 0 }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Currency</Label>
                  <select
                    value={arForm.currency}
                    onChange={(e) => setArForm((f) => ({ ...f, currency: e.target.value }))}
                    className="w-full mt-1 h-10 px-3 border rounded-lg"
                  >
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowARModal(false)}>
                Cancel
              </Button>
              <Button onClick={saveAREntry} data-testid="save-ar-btn">
                Add Invoice
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="import-modal">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4">Import A/R Data</h3>
            
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <Upload className="h-12 w-12 mx-auto text-slate-400 mb-4" />
              <p className="text-sm text-slate-600 mb-2">Upload CSV or Excel file</p>
              <p className="text-xs text-slate-400 mb-4">
                Required columns: company_name, invoice_number, amount, due_date
              </p>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleImportFile}
                className="hidden"
                id="ar-import-file"
              />
              <label htmlFor="ar-import-file">
                <Button asChild>
                  <span>Select File</span>
                </Button>
              </label>
            </div>

            <div className="flex justify-end mt-6">
              <Button variant="outline" onClick={() => setShowImportModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="email-modal">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Mail className="h-5 w-5" />
              {emailForm.company_id ? "Edit" : "Add"} Customer Finance Email
            </h3>
            
            <div className="space-y-4">
              <div className="relative">
                <Label className="text-sm text-slate-600">Company *</Label>
                <Input
                  value={emailForm.company_name}
                  onChange={(e) => {
                    setEmailForm((f) => ({ ...f, company_name: e.target.value }));
                    searchCompanies(e.target.value);
                  }}
                  placeholder="Search company..."
                  className="mt-1"
                />
                {showCompanyDropdown && companyResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {companyResults.map((company) => (
                      <div
                        key={company.id}
                        className="p-2 hover:bg-slate-100 cursor-pointer"
                        onClick={() => {
                          setEmailForm((f) => ({
                            ...f,
                            company_id: company.id,
                            company_name: company.name,
                          }));
                          setShowCompanyDropdown(false);
                        }}
                      >
                        <div className="font-medium">{company.name}</div>
                        {company.email && <div className="text-xs text-slate-500">{company.email}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label className="text-sm text-slate-600">Finance Email *</Label>
                <Input
                  type="email"
                  value={emailForm.finance_email}
                  onChange={(e) => setEmailForm((f) => ({ ...f, finance_email: e.target.value }))}
                  placeholder="finance@company.com"
                  className="mt-1"
                />
                <p className="text-xs text-slate-400 mt-1">This email will receive all SOA and overdue reminders</p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => {
                setShowEmailModal(false);
                setEmailForm({ company_id: "", company_name: "", finance_email: "" });
              }}>
                Cancel
              </Button>
              <Button onClick={saveCustomerEmail} data-testid="save-customer-email-btn">
                Save Email
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Pending Email Action Modal */}
      {showPendingActionModal && selectedPending && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="pending-action-modal">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Inbox className="h-5 w-5 text-amber-600" />
              Resolve Pending Email
            </h3>
            
            <div className="bg-amber-50 rounded-lg p-4 mb-4">
              <p className="font-medium text-amber-900">{selectedPending.company_name}</p>
              <p className="text-sm text-amber-700">{selectedPending.subject}</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-sm text-slate-600">Email Address *</Label>
                <Input
                  type="email"
                  value={pendingAction.new_email}
                  onChange={(e) => setPendingAction((a) => ({ ...a, new_email: e.target.value }))}
                  placeholder="Enter customer's finance email"
                  className="mt-1"
                />
                <p className="text-xs text-slate-400 mt-1">This will be saved for future emails to this customer</p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => {
                setShowPendingActionModal(false);
                setSelectedPending(null);
              }}>
                Cancel
              </Button>
              <Button 
                onClick={handlePendingAction}
                disabled={!pendingAction.new_email}
                data-testid="send-pending-email-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                Save & Send
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Toggle Modal */}
      {showToggleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="toggle-modal">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <ToggleRight className="h-5 w-5 text-primary" />
              {toggleForm.company_id ? "Edit" : "Add"} Customer Email Settings
            </h3>
            
            <div className="space-y-4">
              <div className="relative">
                <Label className="text-sm text-slate-600">Company *</Label>
                <Input
                  value={toggleForm.company_name}
                  onChange={(e) => {
                    setToggleForm((f) => ({ ...f, company_name: e.target.value }));
                    searchCompanies(e.target.value);
                  }}
                  placeholder="Search company..."
                  className="mt-1"
                  disabled={!!toggleForm.company_id}
                />
                {showCompanyDropdown && companyResults.length > 0 && !toggleForm.company_id && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {companyResults.map((company) => (
                      <div
                        key={company.id}
                        className="p-2 hover:bg-slate-100 cursor-pointer"
                        onClick={() => {
                          setToggleForm((f) => ({
                            ...f,
                            company_id: company.id,
                            company_name: company.name,
                          }));
                          setShowCompanyDropdown(false);
                        }}
                      >
                        <div className="font-medium">{company.name}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-slate-900">SOA Emails</span>
                    <p className="text-xs text-slate-500">Send Statement of Account emails to this customer</p>
                  </div>
                  <button
                    onClick={() => setToggleForm((f) => ({ ...f, soa_enabled: !f.soa_enabled }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${toggleForm.soa_enabled ? 'bg-emerald-600' : 'bg-slate-300'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${toggleForm.soa_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-slate-900">Overdue Emails</span>
                    <p className="text-xs text-slate-500">Send overdue reminders to this customer</p>
                  </div>
                  <button
                    onClick={() => setToggleForm((f) => ({ ...f, overdue_enabled: !f.overdue_enabled }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${toggleForm.overdue_enabled ? 'bg-red-600' : 'bg-slate-300'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${toggleForm.overdue_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              </div>

              {(!toggleForm.soa_enabled || !toggleForm.overdue_enabled) && (
                <div className="bg-amber-50 p-3 rounded-lg text-amber-800 text-sm">
                  This customer will NOT receive {!toggleForm.soa_enabled && !toggleForm.overdue_enabled ? "SOA or overdue" : !toggleForm.soa_enabled ? "SOA" : "overdue"} emails even when the global schedule is active.
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => {
                setShowToggleModal(false);
                setToggleForm({ company_id: "", company_name: "", soa_enabled: true, overdue_enabled: true });
              }}>
                Cancel
              </Button>
              <Button onClick={saveCustomerToggle} data-testid="save-customer-toggle-btn">
                Save Settings
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
